import React, { useEffect, useRef } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

const COUNTRY_NAME_TO_ISO2 = {
  'UNITED STATES': 'us',
  'USA': 'us',
  'US': 'us',
  'CANADA': 'ca',
  'CA': 'ca',
  'INDIA': 'in',
  'UNITED KINGDOM': 'gb',
  'GB': 'gb',
  'AUSTRALIA': 'au',
  'NEW ZEALAND': 'nz',
  'CHINA': 'cn',
  'JAPAN': 'jp',
  'SINGAPORE': 'sg',
  'HONG KONG': 'hk',
  'SOUTH KOREA': 'kr',
  'TAIWAN': 'tw',
  'FRANCE': 'fr',
  'SPAIN': 'es',
  'PORTUGAL': 'pt',
  'NETHERLANDS': 'nl',
  'BELGIUM': 'be',
  'SWITZERLAND': 'ch',
  'DENMARK': 'dk',
  'NORWAY': 'no',
  'UNITED ARAB EMIRATES': 'ae',
  'SAUDI ARABIA': 'sa',
  'QATAR': 'qa',
  'KUWAIT': 'kw',
  'BAHRAIN': 'bh',
  'OMAN': 'om',
  'PAKISTAN': 'pk',
  'BANGLADESH': 'bd',
  'SRI LANKA': 'lk',
  'NEPAL': 'np',
  'SOUTH AFRICA': 'za',
  'NIGERIA': 'ng',
  'KENYA': 'ke',
  'MEXICO': 'mx',
  'CHILE': 'cl',
  'COLOMBIA': 'co',
  'GERMANY': 'de',
  'ITALY': 'it',
  'AUSTRIA': 'at',
  'SWEDEN': 'se',
  'FINLAND': 'fi',
  'IRELAND': 'ie',
  'BRAZIL': 'br',
  'ARGENTINA': 'ar',
};

const resolveIso2 = (country) => {
  if (!country) return undefined;
  const normalized = country.trim().toUpperCase();
  if (COUNTRY_NAME_TO_ISO2[normalized]) return COUNTRY_NAME_TO_ISO2[normalized];
  const matchKey = Object.keys(COUNTRY_NAME_TO_ISO2).find((name) =>
    normalized.includes(name)
  );
  return matchKey ? COUNTRY_NAME_TO_ISO2[matchKey] : undefined;
};

const PhoneInputComponent = ({ t, phone, setPhone, phoneError, setPhoneError, country, label }) => {
  const hasNormalizedRef = useRef(false);
  const iso2 = resolveIso2(country);

  // Common logic to parse phone number with fallback countries
  const parsePhoneNumber = (value) => {
    if (!value) return null;

    // Try parsing with + prefix first (for numbers with country codes)
    let phoneNumber = parsePhoneNumberFromString(`+${value}`);

    // If invalid, try parsing with default countries (for legacy numbers without country codes)
    if (!phoneNumber || !phoneNumber.isValid()) {
      phoneNumber = parsePhoneNumberFromString(value, 'US')
                    || parsePhoneNumberFromString(value, 'CA')
                    || parsePhoneNumberFromString(value, 'GB');
    }

    return phoneNumber;
  };

  // Normalize phone number format when data is loaded from backend
  useEffect(() => {
    if (phone && !hasNormalizedRef.current) {
      hasNormalizedRef.current = true;

      const phoneNumber = parsePhoneNumber(phone);

      // If we could parse a country code + national number, ensure it's in the
      // format PhoneInput expects (works even for numbers libphonenumber flags
      // as not "real-world valid", e.g. unassigned/test area codes)
      if (phoneNumber) {
        const formattedForInput = phoneNumber.countryCallingCode + phoneNumber.nationalNumber;

        // Only update if format is different
        if (phone !== formattedForInput) {
          setPhone(formattedForInput);
        }
        if (phoneNumber.isValid()) {
          setPhoneError('');
        }
      }
    }
  }, [phone]);

  const handlePhoneChange = (value, country, e, formattedValue) => {
    hasNormalizedRef.current = true;
    setPhone(value);
  };

  return (
    <div className="input__block">
      <label>
        {t(label || "Primary Phone")} <span className="mandatory">*</span>
      </label>

      <PhoneInput
        key={iso2 || 'default'}
        country={iso2 || undefined}
        priority={iso2 ? (iso2 === 'us' ? { us: 0 } : { [iso2]: 0, us: 1 }) : undefined}
        preferredCountries={iso2 ? [iso2] : undefined}
        value={phone}
        onChange={handlePhoneChange}
        inputProps={{
          name: 'phoneNumber',
          required: true,
          autoFocus: false,
        }}
        inputClass={phoneError ? 'border__red' : ''}
      />

      {phoneError && (
        <span className="error-color-red">{phoneError}</span>
      )}
    </div>
  );
};

export default PhoneInputComponent; 


